import { useCallback, useEffect, useRef, useState } from "react";
import { Search, ChevronLeft, ChevronRight, Loader2, Filter, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { cn } from "../../lib/utils";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { SearchableSelect } from "./SearchableSelect";

const PAGE_SIZES = [25, 50, 100, 200];

function funnelBtn(active) {
  return `rounded p-0.5 transition-colors ${active ? "text-teal-600" : "text-slate-400 hover:text-slate-600"}`;
}

export function HeaderFilterText({ value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(value || "");
  const deb = useRef();
  useEffect(() => { setQ(value || ""); }, [value]);
  const change = (v) => { setQ(v); clearTimeout(deb.current); deb.current = setTimeout(() => onChange(v), 350); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={funnelBtn(!!value)} aria-label="Filter column"><Filter className="h-3.5 w-3.5" /></button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <Input autoFocus value={q} onChange={(e) => change(e.target.value)} placeholder={placeholder || "Filter…"} className="h-8" />
        {q && <button type="button" onClick={() => { setQ(""); onChange(""); }} className="mt-2 text-xs text-slate-500 hover:text-slate-700">Clear filter</button>}
      </PopoverContent>
    </Popover>
  );
}

export function HeaderFilterSelect({ value, options, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={funnelBtn(!!value && value !== "__all__")} aria-label="Filter column"><Filter className="h-3.5 w-3.5" /></button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search…" className="h-9" />
          <CommandList>
            <CommandEmpty>No results</CommandEmpty>
            <CommandGroup>
              <CommandItem value="__all__" onSelect={() => { onChange(""); setOpen(false); }} className="cursor-pointer">
                <Check className={cn("mr-2 h-4 w-4", !value ? "opacity-100 text-teal-600" : "opacity-0")} /> (All)
              </CommandItem>
              {options.map((o) => (
                <CommandItem key={o.value} value={`${o.label} ${o.value}`} onSelect={() => { onChange(o.value); setOpen(false); }} className="cursor-pointer">
                  <Check className={cn("mr-2 h-4 w-4", value === o.value ? "opacity-100 text-teal-600" : "opacity-0")} /> {o.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function DataTable({
  columns,
  load,
  filters = [],
  searchPlaceholder = "Search...",
  hideSearch = false,
  toolbar = null,
  rowActions = null,
  refreshKey = 0,
  testid = "data-table",
  emptyText = "No records found",
  filterValues: controlledFilterValues = null,
  onFilterValuesChange = null,
  onPageItems = null,
}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [internalFilterValues, setInternalFilterValues] = useState({});
  const isControlled = controlledFilterValues !== null;
  const filterValues = isControlled ? controlledFilterValues : internalFilterValues;
  const setFilterValues = (updater) => {
    if (isControlled) {
      onFilterValuesChange(typeof updater === "function" ? updater(controlledFilterValues) : updater);
    } else {
      setInternalFilterValues(updater);
    }
  };
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize, search };
      Object.entries(filterValues).forEach(([k, v]) => {
        if (v && v !== "__all__") params[k] = v;
      });
      const data = await load(params);
      setItems(data.items || []);
      setTotal(data.total || 0);
      onPageItems?.(data.items || []);
    } catch (e) {
      setItems([]);
      setTotal(0);
      onPageItems?.([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, filterValues, load]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { fetchData(); }, 300);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, filterValues, refreshKey, search]);

  const onSearchChange = (v) => { setSearch(v); setPage(1); };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div data-testid={testid} className="space-y-3">
      {(!hideSearch || filters.length > 0 || toolbar) && (
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center">
          {!hideSearch && (
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid={`${testid}-search`}
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-9 pl-9"
            />
          </div>
          )}
          {filters.map((f) => (
            <div key={f.key} className="w-full sm:w-44">
              <SearchableSelect
                testid={`${testid}-filter-${f.key}`}
                value={filterValues[f.key] && filterValues[f.key] !== "__all__" ? filterValues[f.key] : ""}
                onChange={(v) => { setPage(1); setFilterValues((s) => ({ ...s, [f.key]: v || "__all__" })); }}
                options={(f.options || []).map((o) => ({ value: o.value, label: o.label }))}
                placeholder={f.label}
              />
            </div>
          ))}
        </div>
        {toolbar}
      </div>
      )}

      {/* Desktop table */}
      <div className="hidden overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm md:block">
        <div className="max-h-[calc(100vh-300px)] overflow-auto">
        <table className="w-full text-left">
          <thead className="sticky top-0 z-20">
            <tr className="border-b border-slate-200 bg-slate-100">
              {columns.map((c) => (
                <th key={c.key} className="whitespace-nowrap bg-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <div className="flex items-center gap-1">
                    <span>{c.label}</span>
                    {c.headerFilter && (
                      c.headerFilter.type === "text"
                        ? <HeaderFilterText value={filterValues[c.headerFilter.key] || ""} placeholder={c.headerFilter.placeholder} onChange={(v) => { setPage(1); setFilterValues((s) => ({ ...s, [c.headerFilter.key]: v })); }} />
                        : <HeaderFilterSelect value={filterValues[c.headerFilter.key] || ""} options={c.headerFilter.options || []} onChange={(v) => { setPage(1); setFilterValues((s) => ({ ...s, [c.headerFilter.key]: v })); }} />
                    )}
                  </div>
                </th>
              ))}
              {rowActions && <th className="whitespace-nowrap bg-slate-100 px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={columns.length + 1} className="px-4 py-12 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={columns.length + 1} className="px-4 py-12 text-center text-sm text-slate-400">{emptyText}</td></tr>
            ) : (
              items.map((row, i) => (
                <tr key={row.id || i} className="border-b border-slate-100 transition-colors hover:bg-slate-50">
                  {columns.map((c) => (
                    <td key={c.key} className={`px-3 py-2 text-sm ${c.mono ? "font-mono text-xs text-slate-800" : "text-slate-700"}`}>
                      {c.render ? c.render(row) : row[c.key]}
                    </td>
                  ))}
                  {rowActions && <td className="whitespace-nowrap px-3 py-2 text-right">{rowActions(row)}</td>}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      </div>

      {/* Mobile cards */}
      <div className="space-y-3 md:hidden">
        {loading ? (
          <div className="py-10 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400">{emptyText}</div>
        ) : (
          items.map((row, i) => (
            <div key={row.id || i} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="space-y-2">
                {columns.filter((c) => !c.hideMobile).map((c) => (
                  <div key={c.key} className="flex items-start justify-between gap-3">
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-400">{c.label}</span>
                    <span className={`text-right text-sm ${c.mono ? "font-mono text-xs" : ""} text-slate-800`}>
                      {c.render ? c.render(row) : row[c.key]}
                    </span>
                  </div>
                ))}
              </div>
              {rowActions && <div className="mt-3 flex justify-end border-t border-slate-100 pt-3">{rowActions(row)}</div>}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <span>Rows:</span>
          <Select value={String(pageSize)} onValueChange={(v) => { setPage(1); setPageSize(Number(v)); }}>
            <SelectTrigger data-testid={`${testid}-page-size`} className="h-8 w-20"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="ml-2">{total} total</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid={`${testid}-prev`}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-slate-600">Page {page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid={`${testid}-next`}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
